/*
 * Set swap (snippets/hl-set-swap.liquid + hl-set-merged.liquid), on the
 * cart page and in the drawer.
 *
 * When the cart holds every piece of a suite in one colour and the suite is
 * cheaper, the snippet renders a [data-hl-set-offer]. Normally this swaps the
 * pieces for the suite on its own, and the suite's line then says "We merged
 * your ... pieces into this set to save you $X", with Undo.
 *
 * Undo puts the pieces back marked _hl_unmerged. Pieces carrying that mark
 * are never merged on their own again; the snippet shows a "Merge and save"
 * banner under them instead, and the merge happens only when it's pressed.
 * The mark lives on the cart lines, so it holds across pages and reloads.
 *
 * Order matters so a failure never loses anything: the suite is added first
 * and the pieces only come out once it is in; Undo puts the pieces back
 * first, then takes the suite out. A failure shows a message by the button
 * (and the reason in the console).
 *
 * The cart page reloads to re-render; the drawer re-renders its own
 * sections in place without opening itself.
 *
 * Also here, so the two never edit the cart at once: the "Made to order"
 * note on a line that asks for more than are in stock
 * (snippets/hl-stock-split.liquid), which Shopify's checkout shows.
 */
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  // Automatic swaps that failed this visit (the suite sold out since the page
  // rendered, say), so they aren't retried on every re-render.
  var FAILED = 'hl_set_swap_failed_v1';
  var busy = false;

  var failedMem = [];

  function failed() {
    try {
      return (JSON.parse(sessionStorage.getItem(FAILED)) || []).concat(failedMem);
    } catch (e) {
      return failedMem;
    }
  }

  function markFailed(group) {
    if (failedMem.indexOf(group) === -1) failedMem.push(group);
    try {
      sessionStorage.setItem(FAILED, JSON.stringify(failedMem));
    } catch (e) {}
  }

  // Only plain string values go back to Shopify: a null or nested value from
  // an older line would make the whole add fail.
  function cleanProps(p, extra) {
    var out = {};
    if (p && typeof p === 'object') {
      Object.keys(p).forEach(function (k) {
        var v = p[k];
        if (v === null || v === undefined || typeof v === 'object') return;
        out[k] = String(v);
      });
    }
    if (extra) Object.keys(extra).forEach(function (k) { out[k] = extra[k]; });
    return out;
  }

  function showError(scope, err) {
    if (window.console && err) console.warn('[hl-set-swap]', err);
    var el = scope && scope.querySelector('[data-hl-set-error]');
    if (el) el.hidden = false;
  }

  function drawer() {
    return document.querySelector('cart-drawer');
  }

  function onCartPage() {
    return !!document.querySelector('.hlc-page');
  }

  function post(path, body) {
    return fetch(ROOT + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok || j.status) throw j;
        return j;
      });
    });
  }

  // Ask the last call of a swap for the drawer's sections, so it can re-render.
  function withSections(body) {
    var d = drawer();
    if (!onCartPage() && d && d.getSectionsToRender) {
      body.sections = d.getSectionsToRender().map(function (s) { return s.id; });
      body.sections_url = window.location.pathname;
    }
    return body;
  }

  /* ---------- motion ---------- */

  /*
   * A merge reads as one: the pieces' rows glide up into the first and fade
   * while the cart updates, then the suite's row settles in and the "We
   * merged" note unfolds under it. Undo is the reverse. The cart badge pops
   * and rolls its number (3 -> 1) and the bag gives a small squash.
   * Everything settles without overshoot (a critically damped curve) apart
   * from the badge's pop; reduced motion keeps only short fades.
   */
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var SETTLE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
  var ANIM_KEY = 'hl_set_swap_anim_v1';
  var canAnimate = typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';

  function rows(keys) {
    var out = [];
    Array.prototype.forEach.call(document.querySelectorAll('[data-hlc-swipe][data-key]'), function (r) {
      if (!r.offsetParent) return; // the copy on the page that isn't showing
      if (!keys || keys.indexOf(r.getAttribute('data-key')) > -1) out.push(r);
    });
    return out;
  }

  function badges() {
    return Array.prototype.slice.call(document.querySelectorAll('.hl-header__cart-count'));
  }

  function snapshot(kind) {
    var b = badges()[0];
    return {
      kind: kind,
      keys: rows().map(function (r) { return r.getAttribute('data-key'); }),
      count: b ? parseInt(b.textContent, 10) || 0 : 0,
      t: Date.now()
    };
  }

  function finished(anims) {
    return Promise.all(anims.map(function (a) {
      return a && a.finished ? a.finished.catch(function () {}) : null;
    }));
  }

  // Merge, going out: every piece glides up into the first piece and fades.
  function gather(keys) {
    var rs = rows(keys);
    if (!canAnimate || !rs.length) return { done: Promise.resolve(), cancel: function () {} };
    var anims = [];
    var top = rs[0].getBoundingClientRect().top;
    rs.forEach(function (r, i) {
      var dy = top - r.getBoundingClientRect().top;
      if (reduceMotion) {
        anims.push(r.animate([{ opacity: 1 }, { opacity: i ? 0 : 0.6 }], { duration: 200, fill: 'forwards' }));
        return;
      }
      anims.push(r.animate(
        i
          ? [{ transform: 'none', opacity: 1 }, { transform: 'translateY(' + dy + 'px) scale(0.94)', opacity: 0 }]
          : [{ transform: 'none' }, { transform: 'scale(0.98)', offset: 0.5 }, { transform: 'none' }],
        { duration: 480, delay: i * 50, easing: SETTLE, fill: 'forwards' }
      ));
    });
    return { done: finished(anims), cancel: function () { anims.forEach(function (a) { a.cancel(); }); } };
  }

  // Undo, going out: the suite and its note sink back and dim (not vanish,
  // so the drawer is never empty while the cart answers).
  function dissolve(els) {
    els = els.filter(Boolean);
    if (!canAnimate || !els.length) return { done: Promise.resolve(), cancel: function () {} };
    var anims = els.map(function (el) {
      return el.animate(
        reduceMotion
          ? [{ opacity: 1 }, { opacity: 0.4 }]
          : [{ opacity: 1, transform: 'none' }, { opacity: 0.35, transform: 'scale(0.97)' }],
        { duration: reduceMotion ? 200 : 320, easing: SETTLE, fill: 'forwards' }
      );
    });
    return { done: finished(anims), cancel: function () { anims.forEach(function (a) { a.cancel(); }); } };
  }

  // Unfold an element from nothing, padding and all.
  function unfold(el, delay) {
    if (!canAnimate || !el) return;
    if (reduceMotion) {
      el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, delay: delay, fill: 'backwards' });
      return;
    }
    var cs = window.getComputedStyle(el);
    var to = {
      height: el.offsetHeight + 'px',
      paddingTop: cs.paddingTop,
      paddingBottom: cs.paddingBottom,
      marginTop: cs.marginTop,
      marginBottom: cs.marginBottom,
      opacity: 1
    };
    el.style.overflow = 'hidden';
    var a = el.animate([
      { height: '0px', paddingTop: '0px', paddingBottom: '0px', marginTop: '0px', marginBottom: '0px', opacity: 0 },
      to
    ], { duration: 460, delay: delay, easing: SETTLE, fill: 'backwards' });
    a.finished.then(function () { el.style.overflow = ''; }).catch(function () { el.style.overflow = ''; });
  }

  // After the cart re-renders: new rows settle in one after another, then
  // the merged note (or, after Undo, the merge offer) unfolds.
  function enter(snap) {
    if (!snap || !canAnimate) return;
    var fresh = rows().filter(function (r) { return snap.keys.indexOf(r.getAttribute('data-key')) === -1; });
    fresh.forEach(function (r, i) {
      r.animate(
        reduceMotion
          ? [{ opacity: 0 }, { opacity: 1 }]
          : [{ opacity: 0, transform: 'translateY(12px) scale(0.97)' }, { opacity: 1, transform: 'none' }],
        { duration: reduceMotion ? 200 : 520, delay: i * 70, easing: SETTLE, fill: 'backwards' }
      );
    });
    var lead = 120 + fresh.length * 70;
    var note = null;
    var sel = snap.kind === 'undo' ? '[data-hl-set-offer-banner]' : snap.kind === 'merge' ? '[data-hl-set-merged]' : null;
    if (sel) Array.prototype.forEach.call(document.querySelectorAll(sel), function (el) {
      if (!note && el.offsetParent) note = el;
    });
    unfold(note, lead);
  }

  function rollBadgeNumber(b, from, to) {
    var down = to < from; // fewer pieces: the old number leaves upwards
    b.style.overflow = 'hidden';
    b.innerHTML =
      '<span style="display:flex;flex-direction:column;align-items:center;line-height:17px">' +
      '<span>' + (down ? from : to) + '</span><span>' + (down ? to : from) + '</span></span>';
    var reel = b.firstChild;
    reel.animate(
      down
        ? [{ transform: 'translateY(0)' }, { transform: 'translateY(-17px)' }]
        : [{ transform: 'translateY(-17px)' }, { transform: 'translateY(0)' }],
      { duration: 420, delay: 80, easing: SETTLE, fill: 'forwards' }
    ).finished.then(function () {
      b.textContent = to;
      b.style.overflow = '';
    }).catch(function () {
      b.textContent = to;
      b.style.overflow = '';
    });
  }

  // The badge pops and its number rolls from the old count to the new one;
  // the bag beside it gives a small squash.
  function rollBadges(from) {
    if (!canAnimate) return;
    badges().forEach(function (b) {
      var rolling = b._hlRoll && b._hlRoll.wrap.parentNode === b ? b._hlRoll.to : null;
      var to = parseInt(rolling !== null ? rolling : b.textContent, 10);
      if (isNaN(to) || isNaN(from) || to === from || reduceMotion) return;
      if (rolling !== null) {
        // Already rolling to it (assets/hl-qty.js saw the change first).
      } else if (window.hlRollText) {
        // assets/hl-qty.js rolls every count; hand it the old one.
        window.hlRollText(b, from, to);
      } else {
        rollBadgeNumber(b, from, to);
      }
      b.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.35)', offset: 0.35 }, { transform: 'scale(0.96)', offset: 0.7 }, { transform: 'scale(1)' }],
        { duration: 560, easing: 'ease-out' }
      );
      var bag = b.parentElement && b.parentElement.querySelector('svg');
      if (bag) {
        bag.animate(
          [{ transform: 'scale(1)' }, { transform: 'scale(0.86)', offset: 0.3 }, { transform: 'scale(1.05)', offset: 0.65 }, { transform: 'scale(1)' }],
          { duration: 560, easing: 'ease-out' }
        );
      }
    });
  }

  // The cart page reloads to re-render, so its enter animation is handed
  // across the reload.
  function replayAfterReload() {
    var snap = null;
    try {
      snap = JSON.parse(sessionStorage.getItem(ANIM_KEY));
      sessionStorage.removeItem(ANIM_KEY);
    } catch (e) {}
    if (!snap || Date.now() - snap.t > 8000) return;
    enter(snap);
    rollBadges(snap.count);
  }

  function refresh(res, snap) {
    if (onCartPage()) {
      try {
        if (snap) sessionStorage.setItem(ANIM_KEY, JSON.stringify(snap));
      } catch (e) {}
      window.location.reload();
      return;
    }
    var d = drawer();
    if (d && res && res.sections && d.getSectionsToRender) {
      if (d.classList.contains('active') && d.renderContents) {
        d.renderContents(res);
      } else {
        // Same as renderContents, without opening a drawer that was shut.
        d.getSectionsToRender().forEach(function (s) {
          var el = s.selector ? document.querySelector(s.selector) : document.getElementById(s.id);
          var html = res.sections[s.id];
          if (!el || !html) return;
          var src = new DOMParser().parseFromString(html, 'text/html').querySelector(s.selector || '.shopify-section');
          if (src) el.innerHTML = src.innerHTML;
        });
      }
      enter(snap);
    }
    fetch(ROOT + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('hl-cart:changed', { detail: { cart: cart } }));
        var pieces = window.hlCartPieces ? window.hlCartPieces(cart) : cart.item_count;
        Array.prototype.forEach.call(document.querySelectorAll('.hl-header__cart-count'), function (el) {
          el.textContent = pieces;
        });
        if (snap) rollBadges(snap.count);
      })
      .catch(function () {});
  }

  // For other cart edits (assets/hl-cart-undo.js): ask a call for the
  // drawer's sections, and re-draw the drawer and badge from its answer.
  window.hlCartSections = withSections;
  // A line put back slides in like the suite does after a merge.
  window.hlCartRedraw = function (res) {
    refresh(res, snapshot('restore'));
  };

  function swap(offerEl, btn, scope) {
    var group = offerEl.getAttribute('data-group');
    var offer;
    try {
      offer = JSON.parse(offerEl.textContent);
    } catch (e) {
      showError(scope, e);
      return;
    }
    if (!offer || !offer.add || !offer.updates) return;
    offer.add.properties = cleanProps(offer.add.properties);
    busy = true;
    if (btn) btn.disabled = true;
    var snap = snapshot('merge');
    // The pieces glide together while the cart updates; the re-render waits
    // for both.
    var motion = gather(Object.keys(offer.updates));
    var work = post('cart/add.js', { items: [offer.add] }).then(function () {
      return post('cart/update.js', withSections({ updates: offer.updates }));
    });
    Promise.all([work, motion.done])
      .then(function (out) {
        busy = false;
        refresh(out[0], snap);
      })
      .catch(function (err) {
        // Leave the pieces be. An automatic swap isn't retried this visit;
        // a pressed one says so and can be pressed again.
        busy = false;
        motion.cancel();
        if (btn) btn.disabled = false;
        if (btn) showError(scope, err);
        else markFailed(group);
      });
  }

  // A list printed into the page may come back HTML-escaped (&quot;...);
  // read it as it is, and failing that decoded.
  function parseList(text) {
    if (!text) return null;
    var out = null;
    try {
      out = JSON.parse(text);
    } catch (e) {
      try {
        var ta = document.createElement('textarea');
        ta.innerHTML = text;
        out = JSON.parse(ta.value);
      } catch (e2) {
        out = null;
      }
    }
    return Array.isArray(out) && out.length ? out : null;
  }

  // What Undo puts back, best source first: the suite line's stored list as
  // the cart API returns it (raw, never escaped); the same list printed in
  // the page; and failing both, the pieces rebuilt from the catalogue (the
  // suite's recipe in its colour), so Undo works even if the list is gone.
  function piecesFor(mergedEl) {
    var key = mergedEl.getAttribute('data-key');
    var printed = mergedEl.querySelector('[data-hl-set-restore]');
    var rebuilt = mergedEl.querySelector('[data-hl-set-rebuild]');
    var fallback = function (lineKey) {
      return {
        key: lineKey || key,
        list: parseList(printed && printed.textContent) || parseList(rebuilt && rebuilt.textContent)
      };
    };
    return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        var line = null;
        for (var i = 0; i < items.length; i++) {
          if (items[i].key === key) line = items[i];
        }
        if (!line) {
          for (var j = 0; j < items.length; j++) {
            if (items[j].properties && items[j].properties._hl_merged) line = items[j];
          }
        }
        var stored = line && line.properties && line.properties._hl_merged_from;
        var list = typeof stored === 'string' ? parseList(stored) : Array.isArray(stored) ? stored : null;
        if (list) return { key: line.key, list: list };
        return fallback(line && line.key);
      })
      .catch(function () {
        return fallback();
      });
  }

  function undo(mergedEl, btn) {
    busy = true;
    if (btn) btn.disabled = true;
    var stage = 'reading the pieces';
    var snap = snapshot('undo');
    var motion = { done: Promise.resolve(), cancel: function () {} };
    var work = piecesFor(mergedEl)
      .then(function (found) {
        if (!found.list || !found.key) throw new Error('no pieces to put back for line ' + found.key);
        // The pieces go back marked, so they're offered the merge, not given it.
        // Identical entries (a 3 + 1 + 1's two 1 seaters) go back as one line.
        var items = [];
        found.list.forEach(function (r) {
          var props = cleanProps(r.properties, { _hl_unmerged: '1' });
          var sig = r.id + '|' + JSON.stringify(props);
          for (var i = 0; i < items.length; i++) {
            if (items[i]._sig === sig) {
              items[i].quantity += r.quantity;
              return;
            }
          }
          items.push({ _sig: sig, id: r.id, quantity: r.quantity, properties: props });
        });
        items.forEach(function (it) { delete it._sig; });
        var updates = {};
        updates[found.key] = 0;
        stage = 'adding the pieces back';
        // The set and its note fade back while the pieces go in.
        motion = dissolve(rows([found.key]).concat([mergedEl]));
        return post('cart/add.js', { items: items }).then(function () {
          stage = 'removing the set';
          return post('cart/update.js', withSections({ updates: updates }));
        });
      });
    work
      .then(function (res) {
        return motion.done.then(function () { return res; });
      })
      .then(function (res) {
        busy = false;
        refresh(res, snap);
      })
      .catch(function (err) {
        busy = false;
        motion.cancel();
        if (btn) btn.disabled = false;
        showError(mergedEl, { stage: stage, error: err });
      });
  }

  /* ---------- made to order beyond stock ---------- */

  // A line asking for more than are on hand gets a visible "Made to order"
  // property saying how many are made for the customer; Shopify shows it at
  // checkout, on the order and in its emails. Setting a line's properties
  // means sending them all (change.js replaces them), so they're read fresh
  // from the cart rather than from the page. Each change is tried once.
  //
  // It never races the customer: it waits until the steppers have been left
  // alone for a moment and the cart isn't mid-update, and gives way to a tap
  // that lands while it works. Otherwise its answer, carrying the quantity
  // from before the tap, would re-draw the cart over the newer one.
  var NOTE = 'Made to order';
  var IDLE = 1200;
  var splitTried = [];
  var lastTouch = 0;
  var splitTimer = null;

  function touched() {
    lastTouch = Date.now();
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('.hlc-step, .quantity, [data-hlc-remove], [data-hlc-swipe-remove], cart-remove-button')) touched();
  }, true);
  ['input', 'change', 'pointerdown'].forEach(function (ev) {
    document.addEventListener(ev, function (e) {
      if (e.target.closest && e.target.closest('.hlc-step, .quantity, [data-hlc-swipe]')) touched();
    }, true);
  });

  function cartPage() {
    return document.querySelector('hl-cart');
  }

  // Dawn's drawer (cart.js) greys its list while a change is in flight.
  function cartMidUpdate() {
    var page = cartPage();
    return !!(document.querySelector('.cart__items--disabled') || (page && page._busy));
  }

  // How long until the cart has been left alone long enough, or 0 if it has.
  function untilIdle() {
    var wait = IDLE - (Date.now() - lastTouch);
    if (wait <= 0 && cartMidUpdate()) wait = 250;
    return Math.max(0, wait);
  }

  function retrySplitsIn(ms) {
    clearTimeout(splitTimer);
    splitTimer = setTimeout(scan, ms + 20);
  }

  function syncSplit(el) {
    var want;
    try {
      want = JSON.parse(el.textContent);
    } catch (e) {
      return false;
    }
    if (!want || !want.key) return false;
    var tag = want.key + '|' + want.quantity + '|' + want.want;
    if (splitTried.indexOf(tag) > -1) return false;
    var page = cartPage();
    var started = Date.now();
    var interrupted = function () { return lastTouch >= started; };
    var giveWay = function () {
      busy = false;
      splitTried.splice(splitTried.indexOf(tag), 1);
      retrySplitsIn(IDLE);
    };
    splitTried.push(tag);
    busy = true;
    fetch(ROOT + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (interrupted() || cartMidUpdate()) return 'gave-way';
        var line = (cart.items || []).filter(function (i) { return i.key === want.key; })[0];
        // Gone, or changed again since this rendered: the next render says.
        if (!line || line.quantity !== want.quantity) return null;
        var props = cleanProps(line.properties);
        delete props[NOTE];
        if (want.want) props[NOTE] = want.want;
        var body = { id: line.key, quantity: line.quantity, properties: props };
        // The cart page's steppers change lines by key, which this changes:
        // hold them for the moment it takes.
        if (page) {
          page._busy = true;
          page.classList.add('is-busy');
        }
        var done = function () {
          if (page) {
            page._busy = false;
            page.classList.remove('is-busy');
          }
        };
        return post('cart/change.js', page ? body : withSections(body)).then(function (res) {
          done();
          return res;
        }, function (err) {
          done();
          throw err;
        });
      })
      .then(function (res) {
        if (res === 'gave-way') return giveWay();
        busy = false;
        if (!res) return;
        // A tap since this started has its own update on the way, which
        // re-draws the cart with the newer quantity; don't draw over it.
        if (interrupted()) return;
        // The line's key changes with its properties, so the rows re-render.
        if (page && page.refresh) page.refresh();
        else refresh(res, null);
      })
      .catch(function (err) {
        busy = false;
        if (window.console) console.warn('[hl-stock-split]', err);
      });
    return true;
  }

  function scan() {
    if (busy) return;
    var off = failed();
    var offers = document.querySelectorAll('[data-hl-set-offer][data-auto="true"]');
    for (var i = 0; i < offers.length; i++) {
      if (off.indexOf(offers[i].getAttribute('data-group')) === -1) {
        swap(offers[i]);
        return;
      }
    }
    // One line at a time; the re-render brings the next.
    var splits = document.querySelectorAll('[data-hl-split]');
    if (!splits.length) return;
    var wait = untilIdle();
    if (wait) {
      retrySplitsIn(wait);
      return;
    }
    for (var j = 0; j < splits.length; j++) {
      if (syncSplit(splits[j])) return;
    }
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest || busy) return;
    var undoBtn = e.target.closest('[data-hl-set-undo]');
    if (undoBtn) {
      var merged = undoBtn.closest('[data-hl-set-merged]');
      if (merged) undo(merged, undoBtn);
      return;
    }
    var redoBtn = e.target.closest('[data-hl-set-redo]');
    if (redoBtn) {
      var banner = redoBtn.closest('[data-hl-set-offer-banner]');
      var group = banner && banner.getAttribute('data-group');
      var offers = banner ? banner.parentNode.querySelectorAll('[data-hl-set-offer]') : [];
      for (var i = 0; i < offers.length; i++) {
        if (offers[i].getAttribute('data-group') === group) {
          swap(offers[i], redoBtn, banner);
          return;
        }
      }
    }
  });

  var timer = null;
  function soon() {
    clearTimeout(timer);
    timer = setTimeout(scan, 60);
  }

  function init() {
    replayAfterReload();
    scan();
    // The drawer re-renders its HTML after every add; look again when it does.
    if ('MutationObserver' in window) {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType === 1 && (n.matches('[data-hl-set-offer], [data-hl-split]') || n.querySelector('[data-hl-set-offer], [data-hl-split]'))) {
              soon();
              return;
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
